import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { IoIosArrowDown } from "react-icons/io";
import { HiArrowRight } from "react-icons/hi2";

import projects from "../../assets/docs/projects.json";

export default function Projects({ len = projects.length, showIntro = false }) {
  const [collapse, setCollapse] = useState(null);
  const visibleProjects = projects.filter((item) => !item.disabled);

  useEffect(() => {
    projects.forEach((item) => {
      if (!item.thumbnail) return;

      const img = new Image();
      img.src = item.thumbnail;
    });
  }, []);

  const handleCollapse = (idx = null) => {
    setCollapse((current) => (current === idx ? null : idx));
  };

  const disableImage = (event) => event.preventDefault();

  return (
    <>
      {showIntro && (
        <ProjectIntro>
          A few things I’ve been building along the way, each one shaped by
          curiosity, experiments, lessons learned, and the occasional “why is
          this not working?” moment.
        </ProjectIntro>
      )}

      <ProjectContainer>
        {visibleProjects.slice(0, len).map((item, index) => (
          <div key={index} className="project-container">
            <Project className={collapse === index ? "active" : ""}>
              <div
                className="project-clickable"
                onClick={() => handleCollapse(index)}
              />

            <div className="heading">
              <div className="heading-main">
                <span className="project-index">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <div>
                  {item.doc ? (
                    <h4>
                      <span>Experiment — </span>
                      {item.name}
                    </h4>
                  ) : (
                    <h4>{item.name}</h4>
                  )}
                </div>
              </div>

              <IoIosArrowDown />
              </div>
            </Project>

            <AnimatePresence initial={false}>
              {collapse === index && (
                <motion.div
                  className="project-content-wrapper"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    height: {
                      duration: 0.2,
                      ease: "easeOut",
                    },
                    opacity: {
                      duration: 0.15,
                    },
                  }}
                >
                  <div className="project-content">
                    {item.thumbnail && (
                      <div className="photos">
                        <img
                          src={item.thumbnail}
                          alt={`project-screen-${index}`}
                          loading="eager"
                          decoding="async"
                          onContextMenu={disableImage}
                          onDragStart={disableImage}
                        />
                      </div>
                    )}

                    <div className="project-details">
                      <ul>
                        {item.nutshell.map((nutshell, nutshell_idx) => (
                          <li key={nutshell_idx}>{nutshell}</li>
                        ))}
                      </ul>

                      <p>{item.what}</p>
                    </div>

                    {item.nav && (
                      <Link className="nav" to={`/projects/${item.nav}`}>
                        <motion.div
                          whileHover={{ scale: 1.12 }}
                          transition={{
                            type: "spring",
                            stiffness: 400,
                            damping: 8,
                          }}
                        >
                          <HiArrowRight />
                        </motion.div>
                      </Link>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </ProjectContainer>
    </>
  );
}

const ProjectIntro = styled.p`
  max-width: 850px;
  padding: 50px 0 80px;
  margin: 0;
  color: #abababff;
  font-size: clamp(14px, 1.6vw, 18px);
  font-weight: 300;
  line-height: 1.9;
  user-select: none;

  @media (max-width: 768px) {
    padding: 32px 0 42px;
    font-size: 15px;
    line-height: 1.75;
  }
`;

const ProjectContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  .project-container {
    border-bottom: 1px solid #3d3d3d;
    overflow: hidden;
    cursor: pointer;
    position: relative;

    .project-clickable {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1;
    }

    .project-content-wrapper .project-content {
      padding: 0 25px 45px 25px;
      gap: 50px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .photos {
        width: 250px;
        height: 164px;
        z-index: 2;
        flex-shrink: 0;
        overflow: hidden;
        border-radius: 15px;

        img {
          cursor: auto;
          width: 100%;
          height: 100%;
          opacity: 0.9;
          display: block;
          object-fit: fill;
          transition: transform 0.3s ease-in;
        }

        img:hover {
          transform: scale(1.04);
        }
      }

      .project-details {
        min-width: 0;
        flex: 1;
      }

      ul {
        display: flex;
        flex-wrap: wrap;
        list-decoration: none;
        list-style: none;
        gap: 10px;
        margin-bottom: 12px;

        li {
          color: #fff;
          background: #1c1d20;
          font-size: 11px;
          padding: 4px 14px;
          border-radius: 6px;
          user-select: none;
        }
      }

      p {
        font-size: 15px;
        color: #abababff;
        line-height: 1.8em;
        user-select: none;
        font-weight: 300;
      }

      .nav {
        border-radius: 50%;
        z-index: 2;
        flex-shrink: 0;

        div {
          border-radius: 50%;
          cursor: none;
          font-size: 22px;
          color: #000;
          text-decoration: none;
          width: fit-content;
          font-weight: 300;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 18px;
          background: #fff;
          aspect-ratio: 1 / 1;
          flex-shrink: 0;

          svg {
            transform: rotate(-40deg);
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .photos {
      display: none !important;
    }

    .project-content-wrapper .project-content {
      padding: 0 10px 10px 10px !important;
      gap: 20px !important;

      ul {
        gap: 5px !important;
        margin-bottom: 6px !important;
      }

      ul li {
        font-size: 8px !important;
        padding: 4px 7px !important;
      }

      p {
        font-size: 11px !important;
      }
    }

    .nav div {
      font-size: 16px !important;
      padding: 14px !important;
    }
  }
`;

const Project = styled.div`
  width: 100%;
  list-style: none;
  text-decoration: none;
  color: #474747;
  display: flex;
  flex-direction: column;
  transform-origin: center;
  cursor: pointer;

  .heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: rgb(186, 186, 186);
    text-decoration: none;
    transition:
      padding 0.25s ease-in-out,
      color 0.25s ease-in-out;
    padding: 75px 30px;
    line-height: 3.5em;

    .heading-main {
      width: 100%;
      max-width: 900px;
      display: flex;
      align-items: baseline;
      gap: 22px;
    }

    .project-index {
      width: 28px;
      flex-shrink: 0;
      color: #555555;
      font-family: "Space Mono", monospace;
      font-size: 12px;
      font-weight: 300;
      line-height: 1;
      transition:
        color 0.25s ease,
        transform 0.25s ease;
    }

    h4 span {
      color: rgba(244, 244, 244, 0.4);
      font-size: 28px;
    }

    .heading-main > div {
      width: 100%;
      max-width: 820px;
    }

    h4 {
      font-size: 38px;
      font-weight: 400;
      user-select: none;
    }

    svg {
      display: block;
      font-size: 34px;
      flex-shrink: 0;
      transition: transform 0.2s ease-out;
    }
  }

  &.active .heading,
  &:hover .heading {
    padding: 75px 20px;
    color: #fff;

    .project-index {
      color: #888888;
      transform: translateX(2px);
    }
  }

  &.active .heading svg {
    transform: rotate(180deg);
  }

  @media (max-width: 768px) {
    .heading {
      padding: 30px 15px;
      line-height: 1.8em;

      span {
        font-size: 16px;
      }

      .heading-main {
        gap: 12px;
      }

      .project-index {
        width: 24px;
        font-size: 10px;
      }
    }

    .heading h4 {
      font-size: 20px;
    }

    .heading svg {
      font-size: 24px;
    }

    &.active .heading,
    &:hover .heading {
      padding: 30px 10px;
    }
  }
`;
